import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import GlobalStyles from './components/GlobalStyles';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import EventModal from './components/EventModal';
import { Toast } from './components/Shared';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ExecutivePage from './pages/ExecutivePage';
import MembershipPage from './pages/MembershipPage';
import { BlogPage, BlogPostPage } from './pages/BlogPage';
import CalendarPage from './pages/CalendarPage';
import ContactPage from './pages/ContactPage';
import LegalPage from './pages/LegalPage';
import KnowledgeHubPage from './pages/KnowledgeHubPage';
import EventDetailPage from './pages/EventDetailPage';
import CertificatePage from './pages/CertificatePage';
import { BLOG_POSTS, CONTACT_SCRIPT_URL } from './data/websiteData';

// Wrapper to handle individual blog posts
const BlogPostWrapper = ({ openPost }) => {
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);
  return <BlogPostPage post={post} />;
};

const App = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventModal, setEventModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [nl, setNl] = useState('');
  const [nlSt, setNlSt] = useState('idle');

  const [events, setEvents] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/submit')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setEvents(data);
      })
      .catch(err => {
        console.error("Failed to load events:", err);
      });
  }, []);

  const showToast = useCallback((message, type) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToast({ id, message, type });
  }, []);

  const openEvent = useCallback(ev => setEventModal(ev), []);

  const submitNL = async e => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(nl)) { showToast('Enter a valid email address.', 'error'); return; }
    setNlSt('loading');
    try {
      const res = await fetch(CONTACT_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: nl, source: 'Newsletter', timestamp: new Date().toISOString() })
      });
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.status === 'error') throw new Error(data.message || 'API Error');

      setNlSt('success');
      showToast('Subscribed to the IT Bulletin!', 'success');
      setNl('');
    } catch {
      setNlSt('idle');
      showToast('Subscription failed.', 'error');
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = (menuOpen || eventModal) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, eventModal]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <GlobalStyles />

      <Navbar scrolled={scrolled} menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      {/* MOBILE MENU OVERLAY */}
      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, top: 0, background: 'var(--surface)', zIndex: 490, display: 'flex', flexDirection: 'column', padding: '100px 32px 48px', animation: 'af .3s ease', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            {[
              { path: '/', label: 'Home' },
              { path: '/our-story', label: 'About' },
              { path: '/knowledge-hub', label: 'Knowledge Hub' },
              { path: '/committee', label: 'Committee' },
              { path: '/events', label: 'Events' },
              { path: '/verify', label: 'Verify Certificate' },
              { path: '/contact', label: 'Contact' }
            ].map(({ path, label }) => (
              <button key={path} onClick={() => { setMenuOpen(false); navigate(path); }} style={{ display: 'block', width: '100%', padding: '16px 0', background: 'none', border: 'none', borderBottom: '1px solid var(--surface-container-highest)', color: 'var(--on-surface)', fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, textAlign: 'left', cursor: 'pointer', letterSpacing: 'var(--ls-tight)' }}>{label}</button>
            ))}
          </div>
          <button className="btn btn-primary btn-lg" onClick={() => { setMenuOpen(false); navigate('/membership'); }} style={{ width: '100%', justifyContent: 'center', marginTop: 32 }}>Initialize Sync</button>
        </div>
      )}

      <main style={{ animation: 'fadeUp .4s cubic-bezier(.16,1,.3,1) both' }}>
        <Routes>
          <Route path="/" element={<HomePage events={events} openEvent={openEvent} />} />
          <Route path="/our-story" element={<AboutPage />} />
          <Route path="/committee" element={<ExecutivePage />} />
          <Route path="/membership" element={<MembershipPage showToast={showToast} />} />
          <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostWrapper />} />
          <Route path="/events" element={<CalendarPage events={events} />} />
          <Route path="/events/:slug" element={<EventDetailPage events={events} />} />
          <Route path="/contact" element={<ContactPage showToast={showToast} nl={nl} setNl={setNl} nlSt={nlSt} submitNL={submitNL} />} />
          <Route path="/privacy-policy" element={<LegalPage title="Privacy Policy" type="privacy" />} />
          <Route path="/membership-policy" element={<LegalPage title="Membership Policy" type="membership" />} />
          <Route path="/verify" element={<CertificatePage />} />
          <Route path="*" element={<HomePage events={events} />} />
        </Routes>
      </main>

      <Footer nl={nl} setNl={setNl} nlSt={nlSt} submitNL={submitNL} />

      {eventModal && <EventModal event={eventModal} onClose={() => setEventModal(null)} showToast={showToast} />}
      {toast && <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default App;